import React, { useState, useEffect, useRef } from 'react';
import { Modal, Box, Button, IconButton, Typography, Alert } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';

// onFileSelected is optional. When provided, an Upload control is shown next
// to the mic so the user can pick an existing audio file instead of recording.
// AddToQuickTale does not pass it, so that page is unaffected.
const AudioRecordingModal = ({ open, handleClose, onRecordingComplete, onFileSelected }) => {
  const [status, setStatus] = useState('idle'); // 'idle', 'recording', 'stopped'
  const [timer, setTimer] = useState(0);
  const [permissionStatus, setPermissionStatus] = useState('unknown');
  const [permissionError, setPermissionError] = useState('');
  const [mediaBlobUrl, setMediaBlobUrl] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [audioKey, setAudioKey] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);

  const hardResetAudioElement = () => {
    if (!audioRef.current) return;
    try {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    } catch (e) {
      // noop
    }
    try {
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    } catch (e) {
      // noop
    }
  };

  const stopAndClearStream = () => {
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((track) => track.stop());
      } catch (e) {
        // noop
      }
      streamRef.current = null;
    }
  };

  const resetRecording = () => {
    // Stop any active recorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        // noop
      }
    }

    // iOS Safari/WebKit can keep the <audio> element in an error state if we revoke/replace
    // blob URLs without fully resetting the element.
    hardResetAudioElement();

    chunksRef.current = [];

    if (mediaBlobUrl) {
      URL.revokeObjectURL(mediaBlobUrl);
    }

    setMediaBlobUrl(null);
    setRecordedBlob(null);
    setTimer(0);
    setStatus('idle');
    setAudioKey((k) => k + 1);
  };

  // Timer effect
  useEffect(() => {
    let interval;
    if (status === 'recording') {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1);
      }, 1000);
    } else if (status === 'idle') {
      setTimer(0);
    }
    return () => clearInterval(interval);
  }, [status]);

  // Request microphone permission when modal opens
  useEffect(() => {
    const requestMicrophonePermission = async () => {
      if (!open) return;

      try {
        setPermissionError('');

        // Check if we can query permissions (not supported in all browsers)
        if (navigator.permissions && navigator.permissions.query) {
          try {
            const permission = await navigator.permissions.query({ name: 'microphone' });
            setPermissionStatus(permission.state);

            permission.addEventListener('change', () => {
              setPermissionStatus(permission.state);
            });
          } catch (e) {
            console.log('Permissions API not fully supported, using fallback');
          }
        }

        // Always try to get microphone access when modal opens
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });

        setPermissionStatus('granted');
        
        // Store stream for later use
        streamRef.current = stream;

      } catch (error) {
        console.error('Microphone permission error:', error);
        setPermissionStatus('denied');

        if (error.name === 'NotAllowedError') {
          setPermissionError('Microphone access denied. Please allow microphone access and try again.');
        } else if (error.name === 'NotFoundError') {
          setPermissionError('No microphone found. Please connect a microphone and try again.');
        } else {
          setPermissionError('Unable to access microphone. Please check your microphone settings.');
        }
      }
    };

    requestMicrophonePermission();

    // Cleanup on unmount
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [open]);

  const startRecording = async () => {
    try {
      // If user played audio and then re-records, iOS Safari can keep an internal handle.
      // Reset the element *right now* (not only when status==='stopped').
      hardResetAudioElement();

      // If a recording was previously completed, clicking the mic means "re-record":
      // delete the old recording and start fresh.
      if (status === 'stopped') {
        resetRecording();
      }

      // Always reset the counter when starting a new recording session.
      // (If we just called resetRecording(), this is redundant but harmless.)
      setTimer(0);

      // If a preview existed (especially after playback), use a fresh stream to avoid
      // WebKit audio-session weirdness between playback + recording.
      // (We only do this when re-recording; otherwise we reuse the permission-granted stream.)
      if (status === 'stopped') {
        stopAndClearStream();
      }
      if (!streamRef.current) {
        streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      chunksRef.current = [];

      // Create MediaRecorder WITHOUT specifying mimeType
      // Let the browser choose its native format
      const mediaRecorder = new MediaRecorder(streamRef.current, {
        audioBitsPerSecond: 128000 // 128 kbps - good quality, reasonable size
      });
      
      console.log('🎤 MediaRecorder created with mimeType:', mediaRecorder.mimeType);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Create blob with the ACTUAL mime type from the recorder
        const mimeType = mediaRecorder.mimeType;
        const blob = new Blob(chunksRef.current, { type: mimeType });
        
        console.log('🎤 Recording stopped - blob.type:', blob.type);
        console.log('🎤 Recording stopped - blob.size:', blob.size);
        const estimatedDuration = (blob.size * 8) / 128000;
        console.log('🎤 Recording stopped - estimated duration:', estimatedDuration);
        
        setRecordedBlob(blob);
        
        // Create URL for preview
        const url = URL.createObjectURL(blob);
        setMediaBlobUrl(url);
        // Force remount of <audio> to avoid iOS Safari re-use bugs
        setAudioKey((k) => k + 1);
        
        setStatus('stopped');
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Request data every 1000ms (1 second) - required for Safari
      setStatus('recording');

    } catch (error) {
      console.error('Error starting recording:', error);
      setPermissionError('Failed to start recording. Please try again.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const handleSave = () => {
    if (recordedBlob) {
      const sendRecording = async () => {
        let durationSeconds = null;
        try {
          // Compute actual duration locally (more reliable than reading duration from an uploaded URL).
          const arrayBuffer = await recordedBlob.arrayBuffer();
          const AudioContextClass = window.AudioContext || window.webkitAudioContext;
          if (AudioContextClass) {
            const audioCtx = new AudioContextClass();
            const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
            durationSeconds = Math.floor(audioBuffer.duration);
            try {
              await audioCtx.close();
            } catch (e) {
              // noop
            }
          }
        } catch (e) {
          // Fallback to estimate if decode fails
          durationSeconds = Math.floor((recordedBlob.size * 8) / 128000);
        }

        // Backwards compatible: if parent expects one arg, extra args are ignored.
        onRecordingComplete(recordedBlob, durationSeconds);
        handleClose();
      };

      sendRecording();
      
      // Cleanup
      resetRecording();
    }
  };

  const handleModalClose = () => {
    // Cleanup
    resetRecording();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    handleClose();
  };
  
  // Upload an existing audio file instead of recording. Hands the file to the
  // caller and closes the modal, mirroring the Save path.
  const handleFileInputChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    onFileSelected?.(file);
    handleModalClose();
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60).toString().padStart(2, '0');
    const seconds = (time % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  return (
    <Modal open={open} onClose={handleModalClose}>
      <Box sx={style}>
        <Typography variant="body2" component="div" sx={{ mb: 2, textAlign: 'left', fontSize: 16 }}>
          <ol style={{ margin: 0, paddingLeft: '20px' }}>
            <li>To record, tap the <strong>mic icon</strong> <MicIcon sx={{ fontSize: 12, color: '#fff', width: 16, height: 16, backgroundColor: '#00A7CD', borderRadius: 4, }} />.<br />
              <span style={{ fontSize: '0.9em', color: '#666' }}>(wait 2 seconds before speaking)</span>
            </li>
            <li>Press <strong>Stop</strong> <StopIcon sx={{ fontSize: 12, color: '#fff', width: 16, height: 16, backgroundColor: '#9447AF', borderRadius: 4, }} /> to stop recording
              <ul style={{ marginTop: '4px' }}>
                <li>To listen tap the <strong>play</strong> icon <PlayArrowIcon sx={{ fontSize: 18, color: '#000', width: 16, height: 16, backgroundColor: '#f1f3f4', borderRadius: 4, }} />.</li>
                <li>To re-record, tap the <strong>mic icon</strong> <MicIcon sx={{ fontSize: 12, color: '#fff', width: 16, height: 16, backgroundColor: '#00A7CD', borderRadius: 4, }} />.</li>
              </ul>
            </li>
            <li>Tap <strong>SAVE</strong>.</li>
          </ol>
        </Typography>

        {permissionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {permissionError}
          </Alert>
        )}

        {permissionStatus === 'denied' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <Typography variant="body1" color="error" sx={{ mb: 2, textAlign: 'center' }}>
              Microphone access is required to record audio.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Please allow microphone access in your browser settings and refresh the page.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                ml: 11,
              }}
            >
            <IconButton
              onClick={status === 'recording' ? stopRecording : startRecording}
              disabled={permissionStatus === 'denied'}
              sx={{
                backgroundColor: status === 'recording' ? '#9447AF' : '#00A7CD',
                color: 'white',
                width: 80,
                height: 80,
                '&:hover': {
                  backgroundColor: status === 'recording' ? '#7d3f94' : '#008c9e',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#a0a0a0',
                },
              }}
            >
              {status === 'recording' ? <StopIcon sx={{ fontSize: 50 }} /> : <MicIcon sx={{ fontSize: 50 }} />}
            </IconButton>

            {/* Optional: upload an existing audio file instead of recording. */}
            {onFileSelected && (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*"
                  style={{ display: 'none' }}
                  onChange={handleFileInputChange}
                />
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  disabled={status === 'recording'}
                  sx={{
                    color: '#00A7CD',
                    p: 0.5,
                    '&:disabled': { color: '#a0a0a0' },
                  }}
                >
                  <FileUploadOutlinedIcon sx={{ fontSize: 34 }} />
                </IconButton>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: status === 'recording' ? '#a0a0a0' : '#00A7CD',
                  }}
                >
                  Upload
                </Typography>
              </Box>
            )}
            </Box>
            <Typography sx={{ mt: 2 }}>{formatTime(timer)}</Typography>
            {mediaBlobUrl && (
              <audio
                key={audioKey}
                ref={audioRef}
                src={mediaBlobUrl}
                controls
                playsInline
                style={{ marginTop: "20px" }}
              />
            )}
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleModalClose} sx={cancelButtonStyle}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!recordedBlob || permissionStatus === 'denied'} sx={saveButtonStyle}>
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

const cancelButtonStyle = {
  color: "#00A7CD",
  borderColor: "#00A7CD",
  textTransform: 'none',
  marginRight: 1,
  '&:hover': {
    borderColor: '#008c9e',
    backgroundColor: 'rgba(0, 167, 205, 0.04)'
  }
};

const saveButtonStyle = {
  backgroundColor: "#00A7CD",
  color: "#fff",
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#008c9e'
  },
  '&:disabled': {
    backgroundColor: '#e0e0e0',
    color: '#a0a0a0'
  }
};

export default AudioRecordingModal;
